import { Typography, Paper, Button } from "@material-ui/core"
import { useSettingsPageTranslations, type Translations } from "../hooks/use-settings-page-translations"
import { MembershipBasicInfoForm } from "./MembershipBasicInfoForm"
import { useMembership } from "../hooks/use-membership"
import { SettingsPageHeader } from "./SettingsPageHeader"
import { useMembershipActions } from "../hooks/use-membership-actions"
import { MembershipInvitationForm } from "./MembershipInvitationForm"
import type { MembershipViewModel } from "../api/membership"
import { useMembershipPermission } from "../../permissions/hooks/use-memberhip-permission"
import { MembershipPermissionManager } from "./MembershipPermissionManager"
import { useOptions } from "../../options/hooks/use-options"

type SettingsPageSectionProps = {
  membership: MembershipViewModel
  translations: Translations
}

export const NoAccessMemberSettings = ({ membershipId }: { membershipId: string }) => {
  const { membership } = useMembership(membershipId)
  const translations = useSettingsPageTranslations({
    name: membership.fullname,
    member: membership.firstname,
    email: membership.userEmail,
    orgName: membership.orgName,
    fullname: membership.fullname,
  })

  return (
    <div style={{ maxWidth: 750, padding: 12, margin: "auto" }}>
      <SettingsPageHeader membership={membership} translations={translations} />
      <BasicInfoFormSection membership={membership} translations={translations} />
      <PendingInvitationCardSection membership={membership} translations={translations} />
      <MembershipPermissionManagerSection membership={membership} translations={translations} />
      <MembershipInvitationFormSection membership={membership} translations={translations} />
    </div>
  )
}

const BasicInfoFormSection = ({ membership, translations }: SettingsPageSectionProps) => {
  return (
    <Paper component="section" style={{ padding: `20px 16px`, marginTop: 24 }} variant="outlined">
      <MembershipBasicInfoForm
        membership={membership}
        canUpdateBasicInfo={membership.canUpdateDetails}
        translations={translations}
      />
    </Paper>
  )
}

const PendingInvitationCardSection = ({ membership, translations }: SettingsPageSectionProps) => {
  if (!membership.hasPendingInvitation) {
    return null
  }

  return (
    <Paper component="section" variant="outlined" style={{ padding: `20px 16px`, marginTop: 24 }}>
      <PendingInvitationCard membership={membership} translations={translations} />
    </Paper>
  )
}

const MembershipPermissionManagerSection = ({ membership, translations }: SettingsPageSectionProps) => {
  const { permission, ...permissionActions } = useMembershipPermission(membership.id)
  const { customerOptions, membershipOptions, supplierOptions } = useOptions(membership.orgId)

  if (!membership.canUpdatePermissions || !membership.hasPendingInvitation) {
    return null
  }

  return (
    <Paper component="section" variant="outlined" style={{ padding: `0px 16px 20px 16px`, marginTop: 24 }}>
      <MembershipPermissionManager
        membership={membership}
        permission={permission}
        copyPermissions={permissionActions.copyPermissions}
        setAbilitiesInResources={permissionActions.updateAbilitiesInResources}
        setAssociations={permissionActions.updateAssociations}
        updateAbilities={(updates) => permissionActions.updateAbilities({ orgId: membership.orgId, ...updates })}
        customerOptions={customerOptions}
        supplierOptions={supplierOptions}
        membershipOptions={membershipOptions}
        translations={translations}
      />
    </Paper>
  )
}

const MembershipInvitationFormSection = ({ membership, translations }: SettingsPageSectionProps) => {
  if (membership.hasPendingInvitation || !membership.canUpdateInvitationStatus) {
    return null
  }

  return (
    <Paper component="section" variant="outlined" style={{ padding: `20px 16px`, marginTop: 24 }}>
      <MembershipInvitationForm membership={membership} translations={translations} />
    </Paper>
  )
}

const PendingInvitationCard = ({ membership, translations }: SettingsPageSectionProps) => {
  const actions = useMembershipActions()
  const handleCancelInvitation = () => actions.cancelInvitation(membership.id)

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {translations.memberInvited}
      </Typography>
      <Typography variant="body1" style={{ marginBottom: 32 }}>
        {translations.memberInvitedDescription}
      </Typography>
      {membership.canUpdateInvitationStatus ? (
        <Button variant="outlined" onClick={handleCancelInvitation}>
          {translations.cancelInvitationLabel}
        </Button>
      ) : null}
    </>
  )
}
